// @flow

import styles from './SaveSearch.less';

import { savedSearchesPath } from 'src/config';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { isUndefined } from 'lodash';

import { Text, Card, TextLink, Loader } from 'seek-style-guide/react';
import { states } from 'src/store/saveSearch/saveSearch';
import NavLink from 'src/components/NavLink/NavLink';
import SaveIcon from 'src/components/SaveSearchPrompt/icons/SaveIcon';
import ConfirmationIcon from 'src/components/SaveSearchPrompt/icons/ConfirmationIcon';
import AlertIcon from 'src/components/SaveSearchPrompt/icons/AlertIcon';
import InfoIcon from 'src/components/SaveSearchPrompt/icons/InfoIcon';

import SaveSearchButton from 'src/components/SaveSearchButton/SaveSearchButton';

type Props = {
  status: SaveSearchStatus,
  isSearchSaved?: boolean
};

const { SAVED, ERROR, EXCEEDED, SAVING } = states;

const mapStateToProps = ({ saveSearch }) => ({
  status: saveSearch.status,
  isSearchSaved: saveSearch.isSaved
});

const ManageLink = (
  <NavLink component={TextLink} location={savedSearchesPath}>
    <Text className={styles.cta}>Manage your Saved searches</Text>
  </NavLink>
);

class SaveSearch extends Component<Props> {
  props: Props;

  getMessageProps = (): {
    icon: React$Node,
    text: React$Node,
    cta?: React$Node,
    dataAutomation: string
  } => {
    const { status, isSearchSaved } = this.props;

    if (status === SAVED) {
      return {
        icon: <ConfirmationIcon />,
        text: <Text strong>We’ve saved your search.</Text>,
        cta: ManageLink,
        dataAutomation: 'success'
      };
    }

    if (isSearchSaved) {
      return {
        icon: <InfoIcon />,
        text: (
          <Text strong info>
            You’ve saved this search before.
          </Text>
        ),
        cta: ManageLink,
        dataAutomation: 'already-saved'
      };
    }

    if (status === ERROR) {
      return {
        icon: <AlertIcon />,
        text: (
          <Text strong critical>
            There’s something wrong on our end. Try again in a little while.
          </Text>
        ),
        cta: null,
        dataAutomation: 'error'
      };
    }

    if (status === EXCEEDED) {
      return {
        icon: <InfoIcon />,
        text: (
          <Text strong info>
            You can save only up to 25 searches.
          </Text>
        ),
        cta: ManageLink,
        dataAutomation: 'exceeded'
      };
    }

    return {
      icon: <SaveIcon />,
      text: <Text strong>Save this search and get new jobs by email.</Text>,
      cta: this.renderButton(),
      dataAutomation: 'save'
    };
  };

  renderMessage = () => {
    if (isUndefined(this.props.isSearchSaved)) {
      return (
        <div className={styles.loader}>
          <Loader small />
        </div>
      );
    }

    const { dataAutomation, icon, text, cta } = this.getMessageProps();

    return (
      <div data-automation={`saveSearch-${dataAutomation}`}>
        <div className={styles.icon}>{icon}</div>
        {text}
        {cta}
      </div>
    );
  };

  renderButton = () => {
    const { status } = this.props;

    const isSaving = status === SAVING;

    const buttonProps = {
      color: 'blue',
      className: styles.cta,
      'data-automation': 'saveSearchButton',
      loading: isSaving,
      fullWidth: true
    };

    return (
      <SaveSearchButton {...buttonProps}>
        {isSaving ? 'Saving' : 'Save'}
      </SaveSearchButton>
    );
  };

  render() {
    const rootProps = {
      'data-automation': 'sideSavePanel',
      className: styles.root
    };

    return <Card {...rootProps}>{this.renderMessage()}</Card>;
  }
}

export default connect(mapStateToProps)(SaveSearch);
